/**
 * A customer's Statement of Account: every issued statement across their
 * orders, the payments received with dates, the balance, totals and ageing.
 * Prints through the API's PDF, like a single statement does.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Eye } from 'lucide-react';
import { ApiError, api, apiFetch } from '../../lib/api';
import { peso, shortDate } from '../../lib/format';
import {
  FILTER_LABELS,
  STATEMENT_LABELS,
  STATEMENT_TONES,
  parseFilter,
  rangeError,
  statementQuery,
  type StatementFilter,
  type StatementStatus,
} from '../../lib/statement';
import { HelpButton } from '../../components/HelpButton';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  filterClass,
} from '../../components/ui';

type Bucket = 'CURRENT' | 'D31_60' | 'D61_90' | 'D91_120' | 'D120_PLUS';

const BUCKET_LABELS: Record<Bucket, string> = {
  CURRENT: 'Current (0–30)',
  D31_60: '31–60 days',
  D61_90: '61–90 days',
  D91_120: '91–120 days',
  D120_PLUS: 'Over 120 days',
};

interface StatementData {
  customer: {
    id: string;
    code: string;
    name: string;
    tin: string | null;
    addressLine: string | null;
    barangay: string | null;
    city: string | null;
    province: string | null;
    zip: string | null;
    isGovernment: boolean;
    contact: {
      name: string;
      designation: string | null;
      email: string | null;
      mobile: string | null;
      landline: string | null;
    } | null;
  };
  asOf: string;
  basis: 'BILLING_DATE' | 'DUE_DATE';
  rows: {
    id: string;
    code: string;
    type: string;
    billingPeriod: string | null;
    billingDate: string;
    dueDate: string;
    salesOrder: { id: string; code: string; projectName: string };
    billed: string;
    paid: string;
    balance: string;
    awaitingConfirmation: string;
    status: StatementStatus;
    days: number | null;
    payments: { id: string; dateReceived: string; amount: string; reference: string | null }[];
  }[];
  totals: { billed: string; paid: string; outstanding: string; overdue: string };
  ageing: Record<Bucket, string>;
  counts: Record<StatementStatus, number>;
}

const FILTERS: StatementFilter[] = ['ALL', 'PAID', 'PENDING'];

export function CustomerStatementPage(): JSX.Element {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = {
    from: searchParams.get('from') ?? '',
    to: searchParams.get('to') ?? '',
    status: parseFilter(searchParams.get('status')),
  };
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState<'preview' | 'download' | null>(null);

  const setFilter = (key: 'from' | 'to' | 'status', value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value && !(key === 'status' && value === 'ALL')) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const invalid = rangeError(filters);
  const query = statementQuery(filters);

  const statement = useQuery({
    queryKey: ['customer-statement', id, query],
    queryFn: () => api<{ data: StatementData }>(`/customers/${id}/statement?${query}`),
    enabled: Boolean(id) && !invalid,
  });

  const openPdf = async (inline: boolean): Promise<void> => {
    setPdfError(null);
    setPdfBusy(inline ? 'preview' : 'download');
    // Open the tab now, while the click still counts, then point it at the PDF.
    const tab = inline ? window.open('', '_blank') : null;
    try {
      const response = await apiFetch(
        `/customers/${id}/statement/pdf?${query}${query ? '&' : ''}inline=${String(inline)}`,
      );
      if (!response.ok) throw new ApiError(response.status, 'Could not produce the PDF.');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `SOA_${statement.data?.data.customer.code ?? 'customer'}.pdf`;
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      tab?.close();
      setPdfError('Could not produce the PDF.');
    } finally {
      setPdfBusy(null);
    }
  };

  const data = statement.data?.data;
  const customer = data?.customer;
  const address = customer
    ? [customer.addressLine, customer.barangay, customer.city, customer.province, customer.zip]
        .filter(Boolean)
        .join(', ')
    : '';
  const hasOutstanding = Number(data?.totals.outstanding ?? 0) > 0;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={customer ? `/billing?customer=${customer.id}&customerName=${encodeURIComponent(customer.name)}` : '/billing'}
            className="mb-1 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy dark:hover:text-white"
          >
            <ArrowLeft aria-hidden="true" className="h-4 w-4" /> Billing
          </Link>
          <h1 className="text-xl font-bold text-navy dark:text-white">Statement of account</h1>
          <p className="text-sm text-slate-500">
            {customer ? customer.name : 'Loading customer…'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <HelpButton topic="billing" />
          <Button
            variant="secondary"
            busy={pdfBusy === 'preview'}
            disabled={!data || Boolean(invalid)}
            onClick={() => void openPdf(true)}
          >
            <Eye aria-hidden="true" className="h-4 w-4" /> Preview
          </Button>
          <Button
            busy={pdfBusy === 'download'}
            disabled={!data || Boolean(invalid)}
            onClick={() => void openPdf(false)}
          >
            <Download aria-hidden="true" className="h-4 w-4" /> Download PDF
          </Button>
        </div>
      </header>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
            From
            <input
              type="date"
              className={`${filterClass} w-40`}
              value={filters.from}
              onChange={(event) => setFilter('from', event.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
            To
            <input
              type="date"
              className={`${filterClass} w-40`}
              value={filters.to}
              onChange={(event) => setFilter('to', event.target.value)}
            />
          </label>
          <div className="flex flex-col gap-1 text-xs font-medium text-slate-500">
            Status
            <div
              role="radiogroup"
              aria-label="Status"
              className="inline-flex rounded-xl border border-slate-300 p-0.5 dark:border-slate-700"
            >
              {FILTERS.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={filters.status === value}
                  className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                    filters.status === value
                      ? 'bg-navy text-white'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`}
                  onClick={() => setFilter('status', value)}
                >
                  {FILTER_LABELS[value]}
                </button>
              ))}
            </div>
          </div>
          {(filters.from || filters.to || filters.status !== 'ALL') && (
            <Button variant="ghost" size="sm" onClick={() => setSearchParams({}, { replace: true })}>
              Clear filters
            </Button>
          )}
          <p className="ml-auto text-xs text-slate-500">
            {filters.from || filters.to
              ? `Statements dated ${filters.from ? shortDate(`${filters.from}T00:00:00+08:00`) : 'from the start'} to ${
                  filters.to ? shortDate(`${filters.to}T00:00:00+08:00`) : 'today'
                }`
              : 'All dates'}
            {filters.status === 'PENDING' && ' · unpaid and partially paid'}
          </p>
        </div>
      </Card>

      {invalid && <Alert tone="warning">{invalid}</Alert>}
      {pdfError && <Alert tone="critical">{pdfError}</Alert>}
      {statement.error && (
        <Alert tone="critical">
          {statement.error instanceof ApiError ? statement.error.message : 'Could not load the statement.'}
        </Alert>
      )}

      {statement.isLoading && <Skeleton className="h-96 w-full" />}

      {data && customer && (
        <>
          <div className="grid gap-5 lg:grid-cols-3">
            <Card title="Customer">
              <p className="font-semibold text-slate-900 dark:text-white">
                <Link to={`/customers/${customer.id}`} className="hover:underline">
                  {customer.name}
                </Link>
              </p>
              <p className="font-mono text-xs text-slate-500">{customer.code}</p>
              {address && <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{address}</p>}
              {customer.tin && <p className="text-sm text-slate-600 dark:text-slate-300">TIN {customer.tin}</p>}
              {customer.contact && (
                <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                  <p>
                    {customer.contact.name}
                    {customer.contact.designation && (
                      <span className="text-slate-500">, {customer.contact.designation}</span>
                    )}
                  </p>
                  {(customer.contact.mobile ?? customer.contact.landline) && (
                    <p>{customer.contact.mobile ?? customer.contact.landline}</p>
                  )}
                  {customer.contact.email && <p className="break-all">{customer.contact.email}</p>}
                </div>
              )}
              {customer.isGovernment && (
                <div className="mt-2">
                  <StatusChip tone="info">Government</StatusChip>
                </div>
              )}
            </Card>

            <div className="grid gap-4 sm:grid-cols-3 lg:col-span-2">
              <Card>
                <p className="text-xs uppercase tracking-wide text-slate-500">Total billed</p>
                <p className="tabular text-lg font-bold text-slate-900 dark:text-white">{peso(data.totals.billed)}</p>
                <p className="text-xs text-slate-500">
                  {data.rows.length} statement{data.rows.length === 1 ? '' : 's'}
                </p>
              </Card>
              <Card>
                <p className="text-xs uppercase tracking-wide text-slate-500">Total paid</p>
                <p className="tabular text-lg font-bold text-slate-900 dark:text-white">{peso(data.totals.paid)}</p>
                <p className="text-xs text-slate-500">{data.counts.PAID} fully paid</p>
              </Card>
              {/* Not a Card: its white background would win over the navy one. */}
              <section className="rounded-2xl bg-navy p-5 text-white shadow-sm ring-1 ring-navy dark:ring-slate-700">
                <p className="text-xs uppercase tracking-wide text-white/70">Outstanding</p>
                <p className="tabular text-lg font-bold">{peso(data.totals.outstanding)}</p>
                {Number(data.totals.overdue) > 0 && (
                  <p className="text-xs text-white/80">{peso(data.totals.overdue)} overdue</p>
                )}
              </section>

              {hasOutstanding && (
                <Card
                  className="sm:col-span-3"
                  title="Ageing of the outstanding balance"
                  description={`Days counted from the ${data.basis === 'DUE_DATE' ? 'due date' : 'statement date'}, as in Reports → AR Aging.`}
                >
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                    {(Object.keys(BUCKET_LABELS) as Bucket[]).map((bucket) => (
                      <div key={bucket} className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800">
                        <p className="text-xs text-slate-500">{BUCKET_LABELS[bucket]}</p>
                        <p
                          className={`tabular text-sm font-semibold ${
                            Number(data.ageing[bucket]) > 0 && bucket !== 'CURRENT'
                              ? 'text-red-700 dark:text-red-300'
                              : 'text-slate-900 dark:text-white'
                          }`}
                        >
                          {peso(data.ageing[bucket])}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>

          <Card title="Statements">
            {data.rows.length === 0 ? (
              <EmptyState
                title="Nothing to show"
                description={
                  filters.from || filters.to || filters.status !== 'ALL'
                    ? 'No issued statement matches these filters.'
                    : 'No statement has been issued to this customer yet. Drafts and void statements are not included.'
                }
              />
            ) : (
              <TableWrap>
                <thead>
                  <tr>
                    <Th>Date</Th>
                    <Th>Statement no.</Th>
                    <Th>Sales order / project</Th>
                    <Th align="right">Billed</Th>
                    <Th>Payments received</Th>
                    <Th align="right">Balance</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {data.rows.map((row) => (
                    <tr key={row.id}>
                      <Td className="whitespace-nowrap">
                        {shortDate(row.billingDate)}
                        <p className="text-xs text-slate-500">Due {shortDate(row.dueDate)}</p>
                      </Td>
                      <Td>
                        <Link
                          to={`/billing/${row.id}`}
                          className="whitespace-nowrap font-mono text-xs font-medium text-brand hover:underline"
                        >
                          {row.code}
                        </Link>
                        {row.type === 'RECURRING' && (
                          <p className="text-xs text-slate-500">Recurring {row.billingPeriod ?? ''}</p>
                        )}
                      </Td>
                      <Td>
                        <Link
                          to={`/sales-orders/${row.salesOrder.id}`}
                          className="whitespace-nowrap text-xs font-medium hover:underline"
                        >
                          {row.salesOrder.code}
                        </Link>
                        <p className="min-w-40 text-xs text-slate-500">{row.salesOrder.projectName}</p>
                      </Td>
                      <Td align="right" className="tabular whitespace-nowrap">
                        {peso(row.billed)}
                      </Td>
                      <Td>
                        {row.payments.length === 0 ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <ul className="space-y-0.5 text-xs">
                            {row.payments.map((payment) => (
                              <li key={payment.id} className="whitespace-nowrap">
                                <span className="text-slate-500">{shortDate(payment.dateReceived)}</span>{' '}
                                <span className="tabular font-medium">{peso(payment.amount)}</span>
                                {payment.reference && (
                                  <span className="text-slate-500"> · {payment.reference}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                        {Number(row.awaitingConfirmation) > 0 && (
                          <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                            {peso(row.awaitingConfirmation)} awaiting confirmation
                          </p>
                        )}
                      </Td>
                      <Td align="right" className="tabular whitespace-nowrap font-semibold">
                        {peso(row.balance)}
                      </Td>
                      <Td>
                        <StatusChip tone={STATEMENT_TONES[row.status]}>{STATEMENT_LABELS[row.status]}</StatusChip>
                        {row.status === 'OVERDUE' && row.days !== null && (
                          <p className="mt-1 text-xs text-slate-500">{row.days} days</p>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 font-semibold dark:border-slate-700">
                    <Td>Totals</Td>
                    <Td>{''}</Td>
                    <Td>{''}</Td>
                    <Td align="right" className="tabular whitespace-nowrap">
                      {peso(data.totals.billed)}
                    </Td>
                    <Td className="tabular whitespace-nowrap">Paid {peso(data.totals.paid)}</Td>
                    <Td align="right" className="tabular whitespace-nowrap">
                      {peso(data.totals.outstanding)}
                    </Td>
                    <Td>{''}</Td>
                  </tr>
                </tfoot>
              </TableWrap>
            )}
          </Card>
          <p className="text-xs text-slate-500">
            Statements of account, not BIR invoices. Balances are as of {shortDate(data.asOf)}; a payment
            counts once it is confirmed.
          </p>
        </>
      )}
    </div>
  );
}
